import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { fetchGroupStudents } from "@/api/groups";
import { fetchLessonByDate, saveLessonAttendance } from "@/api/journal";
import { ATTENDANCE_MARKABLE, ATTENDANCE_STATUS_LABELS, allowsGrade, type AttendanceStatus } from "@/types/enums";

interface Props {
  groupId: string;
  onClose: () => void;
  onSaved: () => void;
}

interface Row {
  studentId: string;
  studentName: string;
  status: AttendanceStatus;
  grade: number | null;
  comment: string;
}

const today = () => new Date().toISOString().slice(0, 10);

export function MarkLessonDialog({ groupId, onClose, onSaved }: Props) {
  const date = today();
  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState<string | null>(null);

  const { data: students } = useQuery({
    queryKey: ["group-students", groupId],
    queryFn: () => fetchGroupStudents(groupId),
  });

  const { data: lesson } = useQuery({
    queryKey: ["lesson", groupId, date],
    queryFn: () => fetchLessonByDate(groupId, date),
  });

  useEffect(() => {
    if (!students) return;
    setRows(
      students.map((s) => {
        const existing = lesson?.attendanceRecords?.find((r) => r.studentId === s.id);
        return {
          studentId: s.id,
          studentName: s.fullName,
          status: existing?.status ?? "PRESENT",
          grade: existing?.grade ?? null,
          comment: existing?.comment ?? "",
        };
      }),
    );
  }, [students, lesson]);

  const save = useMutation({
    mutationFn: () =>
      saveLessonAttendance({
        groupId,
        date,
        canceled: false,
        records: rows.map((r) => ({
          studentId: r.studentId,
          status: r.status,
          grade: allowsGrade(r.status) ? r.grade : null,
          comment: r.comment,
        })),
      }),
    onSuccess: onSaved,
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Не удалось сохранить отметку"),
  });

  const patch = (studentId: string, patchRow: Partial<Row>) =>
    setRows((prev) =>
      prev.map((r) =>
        r.studentId === studentId
          ? { ...r, ...patchRow, grade: patchRow.status && !allowsGrade(patchRow.status) ? null : (patchRow.grade ?? r.grade) }
          : r,
      ),
    );

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Посещаемость на {new Date(date).toLocaleDateString("ru-RU")}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Ученик</TableCell>
              <TableCell sx={{ width: 200 }}>Статус</TableCell>
              <TableCell sx={{ width: 110 }}>Оценка</TableCell>
              <TableCell sx={{ width: 240 }}>Комментарий родителю</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.studentId}>
                <TableCell>
                  <Typography fontWeight={600} fontSize={13}>
                    {row.studentName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={row.status}
                    onChange={(e) => patch(row.studentId, { status: e.target.value as AttendanceStatus })}
                  >
                    {ATTENDANCE_MARKABLE.map((s) => (
                      <MenuItem key={s} value={s}>
                        {ATTENDANCE_STATUS_LABELS[s]}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    disabled={!allowsGrade(row.status)}
                    value={row.grade ?? ""}
                    onChange={(e) => patch(row.studentId, { grade: e.target.value ? Number(e.target.value) : null })}
                  >
                    <MenuItem value="">—</MenuItem>
                    {[5, 4, 3, 2].map((g) => (
                      <MenuItem key={g} value={g}>
                        {g}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Отправить родителю…"
                    value={row.comment}
                    onChange={(e) => patch(row.studentId, { comment: e.target.value })}
                  />
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography color="text.secondary">В группе нет учеников</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
          Оценка выставляется только при статусе «Пришёл». Комментарий уходит родителю в Telegram после сохранения.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending || rows.length === 0}>
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}
